import type {
  ActivityHandoffProjection,
  ControlPlaneOnboardingStateResponse,
  OnboardingPairingCreatedResponse,
  OnboardingPairingSummary,
  OnboardingPublicErrorCode,
  OnboardingSessionSummary,
} from '@internal/spassu-code-contract';

export type OnboardingOperation = 'idle' | 'pairing' | 'resuming' | 'revoking';

export interface OnboardingViewState {
  activity: ControlPlaneOnboardingStateResponse['activity'] | null;
  session: OnboardingSessionSummary | null;
  pairing: OnboardingPairingSummary | null;
  pairingCommand: OnboardingPairingCreatedResponse | null;
  loading: boolean;
  operation: OnboardingOperation;
  error: string | null;
  errorCode: OnboardingPublicErrorCode | null;
  announcement: string;
}

export type VisibleHandoffStatus =
  | 'not-generated'
  | 'generating'
  | 'awaiting'
  | 'active'
  | 'connected'
  | 'completed'
  | 'legacy-completed'
  | 'expired'
  | 'revoked'
  | 'failed';

export function handoffStatusLabel(status: VisibleHandoffStatus): string {
  const labels: Record<VisibleHandoffStatus, string> = {
    'not-generated': 'Não gerado',
    generating: 'Gerando',
    awaiting: 'Aguardando conexão',
    active: 'Em conexão',
    connected: 'Conectado',
    completed: 'Concluído',
    'legacy-completed': 'Concluído (compatibilidade)',
    expired: 'Expirado',
    revoked: 'Revogado',
    failed: 'Falhou',
  };
  return labels[status];
}

function projectedStatus(
  handoff: ActivityHandoffProjection,
): VisibleHandoffStatus {
  switch (handoff.sessionStatus) {
    case 'completed':
      return 'completed';
    case 'succeeded':
      return 'legacy-completed';
    case 'revoked':
      return 'revoked';
    case 'expired':
      return 'expired';
    case 'failed':
      return 'failed';
    default:
      break;
  }
  switch (handoff.state) {
    case 'connected':
      return 'connected';
    case 'handoff-failed':
      return 'failed';
    case 'onboarding-in-progress':
      return 'active';
    case 'awaiting-local-onboarding':
      return 'awaiting';
    default:
      return 'not-generated';
  }
}

export function visibleHandoffStatus(
  view: OnboardingViewState,
): VisibleHandoffStatus {
  if (view.operation === 'pairing') return 'generating';
  if (view.activity?.handoff) return projectedStatus(view.activity.handoff);
  return view.session
    ? sessionStatus(view.session)
    : pairingStatus(view.pairing);
}

function pairingStatus(
  pairing: OnboardingPairingSummary | null,
): VisibleHandoffStatus {
  if (pairing?.status === 'created') return 'awaiting';
  if (pairing?.status === 'expired') return 'expired';
  if (pairing?.status === 'blocked') return 'failed';
  return 'not-generated';
}

function sessionStatus(
  session: OnboardingSessionSummary,
): VisibleHandoffStatus {
  if (session.status === 'completed') return 'completed';
  if (session.status === 'succeeded') return 'legacy-completed';
  if (session.status === 'expired') return 'expired';
  if (session.status === 'revoked') return 'revoked';
  if (session.status === 'failed') return 'failed';
  if (session.status === 'connected') return 'connected';
  return 'active';
}

export type OnboardingAction =
  | { type: 'loading' }
  | { type: 'loaded'; value: ControlPlaneOnboardingStateResponse }
  | {
      type: 'operation';
      operation: OnboardingOperation;
      announcement: string;
    }
  | {
      type: 'pairing';
      value: OnboardingPairingCreatedResponse | OnboardingPairingSummary;
    }
  | { type: 'session-status'; value: OnboardingSessionSummary }
  | { type: 'revoked'; value: OnboardingSessionSummary }
  | {
      type: 'error';
      message: string;
      code?: OnboardingPublicErrorCode;
    }
  | { type: 'announce'; message: string };

export const initialOnboardingState: OnboardingViewState = {
  activity: null,
  session: null,
  pairing: null,
  pairingCommand: null,
  loading: true,
  operation: 'idle',
  error: null,
  errorCode: null,
  announcement: 'Carregando conexão da atividade.',
};

function pairingSummary(
  value: OnboardingPairingCreatedResponse | OnboardingPairingSummary,
): OnboardingPairingSummary {
  return {
    id: value.id,
    activityId: value.activityId,
    status: value.status,
    expiresAt: value.expiresAt,
    createdAt: value.createdAt,
    codeAvailable: false,
  };
}

function loadedState(
  state: OnboardingViewState,
  value: ControlPlaneOnboardingStateResponse,
  preserveCommand: boolean,
): OnboardingViewState {
  const preserveConflict =
    preserveCommand && state.errorCode === 'ONBOARDING_ACTIVE_SESSION_CONFLICT';
  const nextState: OnboardingViewState = {
    ...state,
    activity: value.activity,
    session: value.session,
    pairing: value.pairing,
    pairingCommand:
      preserveCommand && value.pairing?.status === 'created'
        ? state.pairingCommand
        : null,
    loading: false,
    error: preserveConflict ? state.error : null,
    errorCode: preserveConflict ? state.errorCode : null,
    announcement: state.announcement,
  };
  const previousSignal = handoffSignal(state);
  const nextSignal = handoffSignal(nextState);
  return {
    ...nextState,
    announcement:
      previousSignal === nextSignal && preserveCommand
        ? state.announcement
        : loadedAnnouncement(nextState),
  };
}

function handoffSignal(state: OnboardingViewState): string {
  return `${visibleHandoffStatus(state)}:${
    state.activity?.handoff?.alert?.code ?? 'no-alert'
  }`;
}

function loadedAnnouncement(state: OnboardingViewState): string {
  if (state.activity?.handoff?.alert) {
    return 'A conexão está atrasada. Verifique a saúde do agente.';
  }
  if (state.activity?.handoff || state.session) {
    // Deliberately silent: announcing `handoff_state` (with a session fallback,
    // never reading expiry) contradicted the canonical projection rendered beside
    // it — an activity persisted `connected` with a past `expires_at` announced
    // "Conectado" next to "Expirada". ADR-009 forbids a frontend deriving this.
    // The connection state is announced by the page from the projection; this
    // region keeps only transient operation feedback.
    return '';
  }
  if (state.pairing?.status === 'created') {
    return 'Pareamento aguardando o terminal.';
  }
  return 'Nenhum pareamento ativo.';
}

function errorState(
  state: OnboardingViewState,
  message: string,
  code?: OnboardingPublicErrorCode,
): OnboardingViewState {
  return {
    ...state,
    loading: false,
    operation: 'idle',
    error: message,
    errorCode: code ?? null,
    announcement: message,
  };
}

function pairingState(
  state: OnboardingViewState,
  value: OnboardingPairingCreatedResponse | OnboardingPairingSummary,
): OnboardingViewState {
  return {
    ...state,
    session: null,
    pairing: pairingSummary(value),
    pairingCommand: value.codeAvailable ? value : null,
    operation: 'idle',
    error: null,
    errorCode: null,
    announcement: value.codeAvailable
      ? 'Código de pareamento gerado.'
      : 'Pareamento já existente; o código não pode ser reexibido.',
  };
}

function revokedState(
  state: OnboardingViewState,
  session: OnboardingSessionSummary,
): OnboardingViewState {
  return {
    ...state,
    activity: state.activity
      ? { ...state.activity, handoff: undefined }
      : state.activity,
    session,
    pairing: null,
    pairingCommand: null,
    operation: 'idle',
    error: null,
    errorCode: null,
    announcement: 'Credencial revogada.',
  };
}

export function onboardingReducer(
  state: OnboardingViewState,
  action: OnboardingAction,
): OnboardingViewState {
  switch (action.type) {
    case 'loading':
      return { ...initialOnboardingState };
    case 'loaded':
      return loadedState(state, action.value, false);
    case 'operation':
      return {
        ...state,
        operation: action.operation,
        error: null,
        errorCode: null,
        announcement: action.announcement,
      };
    case 'pairing':
      return pairingState(state, action.value);
    case 'session-status':
      return { ...state, session: action.value };
    case 'revoked':
      return revokedState(state, action.value);
    case 'announce':
      return { ...state, announcement: action.message };
    case 'error':
      return errorState(state, action.message, action.code);
    default:
      return state;
  }
}
